<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import V2CatalogActionList from '@/components/v2/catalog/panels/V2CatalogActionList.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import type { ICatalogMenuEntry } from '@/domain/catalog/catalog-menus';
  import { capitalize } from '@/domain/catalog/catalog-status-text';
  import {
    choicesCount,
    effectiveRule,
    groupRuleText,
  } from '@/domain/catalog/option-groups';
  import type { IOptionLinkRow } from '@/domain/catalog/product-form';

  /**
   * One option group of the product (plan §5.13): name · rule · «4 opciones»
   * · «Se usa en 8 productos», hidden-here and unsaved states, and its ⋯.
   */
  const props = defineProps<{
    row: IOptionLinkRow;
    /** Products that link the group (0 for drafts). */
    usedIn: number;
    first: boolean;
    last: boolean;
    readOnly: boolean;
    /** The product is being saved: a group not saved yet cannot be edited until it is. */
    saving: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'edit'): void;
    (e: 'toggle'): void;
    (e: 'move', delta: -1 | 1): void;
    (e: 'copy'): void;
    (e: 'remove'): void;
  }>();

  type TGroupAction = 'edit' | 'toggle' | 'up' | 'down' | 'copy' | 'remove';

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const group = computed(() => props.row.draft ?? props.row.option);
  const title = computed(
    () => group.value?.title?.trim() || t('menuPage.groups.untitled')
  );

  const rule = computed(() => {
    const source = group.value;
    if (!source) return '';
    const text = groupRuleText(
      effectiveRule(
        {
          type: source.type,
          multiple: source.multiple,
          mandatory: source.mandatory,
          min: source.min ?? null,
          max: source.max ?? null,
        },
        props.row.overrides
      )
    );
    return t(text.key, text.params);
  });

  const meta = computed(() => {
    const parts = [capitalize(rule.value)];
    const count = choicesCount(props.row);
    parts.push(tc('menuPage.groups.choices', count, { n: count }));
    if (props.usedIn > 1)
      parts.push(
        tc('menuPage.groups.usedIn', props.usedIn, { n: props.usedIn })
      );
    return parts.filter(Boolean).join(' · ');
  });

  const { online } = useCatalogPage();

  const entries = computed<ICatalogMenuEntry<TGroupAction>[]>(() => {
    const entry = (
      key: TGroupAction,
      labelKey: string,
      icon: string,
      danger = false,
      write = false
    ): ICatalogMenuEntry<TGroupAction> => ({
      key,
      labelKey,
      icon,
      danger,
      write,
      plan: null,
    });
    const list = [
      // A saved group's edits commit at once (it is shared): not offline.
      entry(
        'edit',
        'menuPage.groups.edit',
        'pencil-outline',
        false,
        !props.row.draft
      ),
      entry(
        'toggle',
        props.row.active
          ? 'menuPage.groups.hideHere'
          : 'menuPage.groups.showHere',
        props.row.active ? 'eye-off-outline' : 'eye-outline'
      ),
    ];
    if (!props.first)
      list.push(entry('up', 'menuPage.editor.size.up', 'arrow-up'));
    if (!props.last)
      list.push(entry('down', 'menuPage.editor.size.down', 'arrow-down'));
    if (!props.row.draft)
      list.push(entry('copy', 'menuPage.groups.copyHere', 'content-copy'));
    list.push(
      entry('remove', 'menuPage.groups.remove', 'link-variant-off', true)
    );
    return list;
  });

  function onAction(key: string, close: () => void) {
    close();
    // A menu opened before the save: a draft is not edited until it is saved.
    if (props.saving && props.row.draft) return;
    if (key === 'edit') emit('edit');
    else if (key === 'toggle') emit('toggle');
    else if (key === 'up') emit('move', -1);
    else if (key === 'down') emit('move', 1);
    else if (key === 'copy') emit('copy');
    else if (key === 'remove') emit('remove');
  }
</script>

<template>
  <div
    class="v2-catalog-option-group-row"
    :class="{ 'is-hidden': !row.active }"
  >
    <div class="group-text">
      <span class="group-title" :title="title">{{ title }}</span>
      <span class="group-meta">{{ meta }}</span>
      <span v-if="!row.active" class="group-state">{{
        t('menuPage.groups.hiddenHere')
      }}</span>
      <span v-if="row.draft" class="group-state is-draft">{{
        t('menuPage.groups.unsaved')
      }}</span>
      <span v-if="row.overrides" class="group-state">{{
        t('menuPage.groups.overridesHere', { rule })
      }}</span>
    </div>
    <PosUiPopover
      v-if="!readOnly"
      class="group-more"
      placement="bottom"
      align="end"
    >
      <template #trigger="{ toggle }">
        <button
          type="button"
          class="more-trigger"
          :aria-label="t('menuPage.editor.size.actions', { name: title })"
          :disabled="saving && !!row.draft"
          @click="toggle"
        >
          <i class="mdi mdi-dots-horizontal" />
        </button>
      </template>
      <template #default="{ close }">
        <V2CatalogActionList
          class="more-list"
          :entries="entries"
          :online="online"
          @select="onAction($event, close)"
        />
      </template>
    </PosUiPopover>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-option-group-row {
    @apply tw-flex tw-min-h-[56px] tw-items-center tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-py-2 tw-pl-4 tw-pr-2;

    &:first-child {
      @apply tw-border-t-0;
    }

    .group-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;
    }

    .group-title {
      @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
    }

    .group-meta {
      @apply tw-text-xs tw-leading-5 tw-text-neutral-500;
    }

    .group-state {
      @apply tw-text-xs tw-font-medium tw-text-neutral-600;

      &.is-draft {
        @apply tw-text-amber-800;
      }
    }

    &.is-hidden {
      .group-title,
      .group-meta {
        opacity: 0.55;
      }
    }

    .group-more {
      @apply tw-ml-1 tw-flex-none;
    }

    .more-trigger {
      @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-xl tw-text-neutral-500;

      &:hover:not(:disabled) {
        @apply tw-bg-neutral-100 tw-text-neutral-800;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      &:disabled {
        @apply tw-cursor-not-allowed tw-bg-neutral-100 tw-text-neutral-400;
      }
    }

    .more-list {
      width: 240px;
    }
  }
</style>
